import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEO, PAGE_SEO } from "@/components/SEO";

const sections = [
  {
    title: "1. Parties, Definitions, and Acceptance",
    content: `These Terms of Service ("Terms") form a legally binding agreement between you ("you", "User") and Lifely Holdings Limited (NZBN 9429053450883) ("Lifely", "we", "us", "our") regarding your access to and use of lifely.guide, app.lifely.guide, and any related services, features, content, software, and applications (collectively, the "Service").

Acceptance. By accessing, creating an account, subscribing, or otherwise using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, do not use the Service and do not subscribe.

We may update these Terms from time to time. Changes apply when posted. Your continued use constitutes acceptance of updated Terms.

"Consumer" means an individual using the Service for personal use (not in trade).
"User Content" means all information, notes, files, entries, and other content you input into the Service.
"Insights" means informational materials we publish from time to time, which are not advice.`,
  },
  {
    title: "2. Eligibility and NZ-Only Service Positioning",
    content: `You must be at least 18 years old and have capacity to contract. The Service is operated from New Zealand. While we may permit access from other jurisdictions, the Service is offered on a New Zealand law basis and is not tailored to any specific foreign regulatory regime.

NZ-only eligibility clause (positioning). The Service is intended to be contracted for and governed under New Zealand law. If your local law prevents you from entering into these Terms under New Zealand law, you must not use the Service.

You are responsible for ensuring your use is lawful where you are located.`,
  },
  {
    title: "3. Service Description; No Advice; No Professional Relationship",
    content: `The Service helps you organise information for personal continuity planning. It does not provide legal, tax, financial, investment, estate planning, or crypto advice. Any templates, checklists, or Insights are general information only.

• We are not a law firm and do not provide legal services.
• We are not a financial adviser or broker.
• We are not a custodian, wallet provider, bank, trustee, executor, nominee, or insurer.

You must obtain independent professional advice appropriate to your circumstances.`,
  },
  {
    title: "4. No Fiduciary Duty, No Custody, and No Agency",
    content: `You acknowledge and agree that no fiduciary, trustee, adviser-client, custodian-client, agency, partnership, joint venture, or employment relationship is created. We do not owe duties of loyalty, care, or trust beyond those required by law.

We do not take custody of assets or act on your instructions. We do not contact your nominee(s), trigger transfers, or execute any actions in the real world.`,
  },
  {
    title: "5. Crypto and Digital Asset High-Risk Terms",
    content: `Crypto assets and related technologies are inherently risky. The Service does not store private keys, seed phrases, or control wallets. You remain solely responsible for security, access, and transaction decisions.

• Loss of credentials can lead to permanent loss of assets with no recovery.
• Blockchains, wallets, exchanges, and protocols may fail, be exploited, fork, or become inaccessible.
• Smart contracts may contain vulnerabilities.
• Regulatory treatment varies and changes frequently; taxes and reporting obligations may apply.

No liability for digital asset loss. To the maximum extent permitted by law, we are not responsible for any loss of crypto or other digital assets, loss of access, forks, exploits, hacks, protocol failures, exchange insolvency, or user error.`,
  },
  {
    title: "6. Continuity and Estate Planning Carve-Outs",
    content: `The Service provides organisational tools only. We do not verify the legal validity of wills, powers of attorney, trusts, nominations, beneficiary designations, or instructions. We do not guarantee enforceability or recognition in any jurisdiction.

• We do not confirm nominee identity, authority, or willingness to act.
• We do not ensure your arrangements are complete or effective.
• We do not ensure your instructions will be followed by third parties.

No liability for continuity outcomes. We are not liable for estate disputes, family provision claims, inheritance challenges, or third-party failures to act (including banks, exchanges, registries, insurers, or nominees).`,
  },
  {
    title: "7. Nature of Service",
    content: `Lifely is a technology platform for organizing digital intent. We do not provide legal, financial, or estate planning advice. The release of information via continuity features is an automated process based on User-defined parameters. Lifely is not responsible for the legal validity of documents stored within the app or for the actions taken by Nominees once data is released.`,
  },
  {
    title: "8. Service Availability and Best Efforts",
    content: `While we strive for high availability, Lifely provides its 'Check-in' and 'Notification' services on a 'best efforts' basis. We are not liable for delayed notifications resulting from third-party email provider issues, ISP outages, or incorrect contact information provided by the User.

Users are responsible for ensuring their contact information and nominee details are accurate and up to date.`,
  },
  {
    title: "9. Data Security Tiers and No-Access Guarantee",
    content: `Standard Plan:
Data is encrypted during transit and at rest using industry-standard AES-256 encryption. Data is stored on our managed cloud infrastructure (Cloudflare D1).

Plus Plan:
Plus Plan security features will be detailed upon launch. The Plus Plan will offer enhanced security options which may include additional encryption and access controls. Users acknowledge that enhanced security features may limit Lifely's ability to assist with account recovery.`,
  },
  {
    title: "10. User Content, Prohibited Content, and Security Choices",
    content: `You are solely responsible for all User Content. You represent and warrant you have rights to input it and that it is lawful.

Do not store secrets. You should not input private keys, seed phrases, master passwords, or other highly sensitive credentials. If you choose to input such information, you do so at your own risk.

• No illegal content, malware, phishing, or attempts to gain unauthorised access.
• No infringement of intellectual property or privacy rights of others.
• No content that would require us to be licensed or regulated (e.g., custody).`,
  },
  {
    title: "11. Subscriptions, Payments, and Stripe Short Form",
    content: `Subscriptions may be offered on a recurring basis. Pricing, billing frequency, and features are described at checkout and may change prospectively.

Payments are processed by Stripe. We do not store card numbers. Authentication and membership management are provided by Memberstack.

Stripe-compatible short form (for checkout). By confirming your payment, you authorise Lifely to charge your selected payment method for the subscription price, taxes (if any), and any renewals until you cancel.

Subscriptions renew automatically unless cancelled before the renewal date. No refunds except where required by law.`,
  },
  {
    title: "12. Third-Party Services and Infrastructure",
    content: `The Service is hosted on Cloudflare infrastructure and integrates third-party services (including Stripe and Memberstack). These providers have their own terms and privacy practices.

Third-party carve-out. To the maximum extent permitted by law, we are not responsible for outages, failures, security incidents, or data loss caused by third-party providers or internet infrastructure outside our reasonable control.`,
  },
  {
    title: "13. Availability, Bugs, and Warranty Disclaimers",
    content: `The Service is provided "as is" and "as available". We do not warrant uninterrupted, error-free, or secure operation. Software may contain bugs and may be updated or changed.

• No warranty of fitness for a particular purpose.
• No warranty that data will never be lost or corrupted.
• No warranty that the Service will meet all requirements or be compatible with all devices.`,
  },
  {
    title: "14. Force Majeure",
    content: `We are not liable for any failure or delay due to events beyond our reasonable control, including natural disasters, war, terrorism, government action, regulatory change, cyber attacks, internet outages, and third-party infrastructure failures.`,
  },
  {
    title: "15. Limitation of Liability; Cap; Exclusions",
    content: `To the maximum extent permitted by law, we exclude liability for indirect, consequential, special, exemplary, or punitive damages, loss of profits, loss of opportunity, loss of data, and loss arising from digital assets.

Aggregate cap. Our total aggregate liability for any claim relating to the Service is limited to the amount you paid to us in the 12 months immediately before the event giving rise to the claim. If you paid nothing, liability is capped at NZD $100.

Some jurisdictions do not allow certain exclusions. In those cases, our liability is limited to the minimum extent permitted by applicable law.`,
  },
  {
    title: "16. Indemnity",
    content: `You agree to indemnify and hold harmless Lifely, its directors, officers, contractors, and agents from any claim, demand, loss, or expense (including reasonable legal fees) arising from your use of the Service, your User Content, or your breach of these Terms.`,
  },
  {
    title: "17. Termination, Suspension, and Data Effects",
    content: `You may cancel at any time. We may suspend or terminate access if we believe you have violated these Terms, if required by law, or to protect the Service.

Upon termination, access may cease immediately. Some data may remain in backups for a limited time as described in the Privacy Policy.`,
  },
  {
    title: "18. US-Specific: Class Action Waiver and Jury Waiver",
    content: `To the extent permitted by law, you agree that disputes will be brought only in your individual capacity, and not as a plaintiff or class member in any purported class, collective, consolidated, or representative proceeding.

You waive any right to a trial by jury.`,
  },
  {
    title: "19. Disputes: Mandatory Arbitration (NZ), Venue, and Injunctive Relief",
    content: `Except where prohibited by mandatory law, any dispute arising out of or relating to the Service or these Terms shall be resolved by binding arbitration seated in New Zealand under the Arbitration Act 1996 (NZ), before a single arbitrator, in English, and on a confidential basis.

Either party may seek urgent injunctive or equitable relief in New Zealand courts to protect intellectual property or prevent unauthorised access, pending arbitration.

If a court finds arbitration unenforceable for a particular dispute, you agree that dispute will be heard exclusively in the courts of New Zealand, unless mandatory local law requires otherwise.`,
  },
  {
    title: "20. Consumer Law and Non-Excludable Rights",
    content: `Nothing in these Terms excludes or limits rights that cannot be excluded under applicable law. For New Zealand Consumers, the Consumer Guarantees Act 1993 may apply.`,
  },
  {
    title: "21. Changes to Terms",
    content: `We may update these Terms from time to time to reflect changes in our practices, technologies, legal requirements, or other factors. When we make changes, we will update the "Effective date" at the top of these Terms. Changes apply when posted.

We encourage you to review these Terms periodically. Your continued use of the Service after changes are posted constitutes your acceptance of the updated Terms.`,
  },
  {
    title: "22. General Provisions",
    content: `Severability: if one part is unenforceable, the rest remains effective.

Assignment: we may assign these Terms as part of a restructure or sale; you may not assign without consent.

Entire agreement: these Terms and the Privacy Policy form the entire agreement.

No waiver: failure to enforce a provision is not a waiver.`,
  },
];

export default function Terms() {
  return (
    <div className="pt-24">
      <SEO {...PAGE_SEO.terms} />
      <section className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Terms of Service</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
              Legal
            </p>
            <h1 className="font-serif text-4xl md:text-5xl text-foreground">
              Terms of Service
            </h1>
            <p className="mt-4 text-muted-foreground">
              Lifely Holdings Limited (NZBN 9429053450883)
            </p>
            <p className="mt-1 text-muted-foreground">
              Effective date: 28 February 2026
            </p>
            <p className="mt-1 text-sm text-muted-foreground/70">
              Websites: lifely.guide / app.lifely.guide
            </p>
          </motion.div>

          <div className="mt-14 space-y-10">
            {sections.map((section, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-lg font-semibold text-foreground mb-3">
                  {section.title}
                </h2>
                <div className="text-muted-foreground leading-[1.8] whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 pt-8 border-t border-border/50">
            <p className="text-sm text-muted-foreground/70">
              Questions about these terms? Contact us at{" "}
              <a
                href="mailto:privacy@lifely.guide"
                className="text-primary hover:underline"
              >
                privacy@lifely.guide
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
